import type { PortalProjectsInfo } from './portalSession';

export interface PortalProjectOption {
  id: string;
  name: string;
  /** Portal `projList[].project_image` URL */
  projectImage?: string;
}

function readProjectId(raw: Record<string, unknown>): string {
  return String(
    raw.id ?? raw.projectId ?? raw.projId ?? raw._id ?? '',
  ).trim();
}

function readProjectName(raw: Record<string, unknown>, id: string): string {
  const name = String(
    raw.name ?? raw.projectName ?? raw.projName ?? raw.title ?? '',
  ).trim();
  return name || id;
}

function readProjectImage(raw: Record<string, unknown>): string | undefined {
  const url = String(
    raw.project_image ??
      raw.projectImage ??
      raw.image ??
      raw.imageUrl ??
      raw.image_url ??
      raw.thumbnail ??
      '',
  ).trim();
  return url || undefined;
}

/**
 * Normalize portal `projList` entries to `{ id, name }`.
 */
export function normalizePortalProjectList(
  projList: PortalProjectsInfo['projList'],
): PortalProjectOption[] {
  if (!Array.isArray(projList)) {
    return [];
  }

  const seen = new Set<string>();
  const options: PortalProjectOption[] = [];

  for (const item of projList) {
    if (item == null) {
      continue;
    }
    if (typeof item === 'string' || typeof item === 'number') {
      const id = String(item).trim();
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      options.push({ id, name: id });
      continue;
    }
    if (typeof item !== 'object') {
      continue;
    }
    const raw = item as Record<string, unknown>;
    const id = readProjectId(raw);
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    const projectImage = readProjectImage(raw);
    options.push({
      id,
      name: readProjectName(raw, id),
      ...(projectImage ? { projectImage } : {}),
    });
  }

  return options;
}
